import React, { forwardRef, useState } from 'react';
import IconButton from '@mui/material/IconButton';
import FastRewindIcon from '@mui/icons-material/FastRewind';
import FastForwardIcon from '@mui/icons-material/FastForward';
import Slider from '@mui/material/Slider';
import Tooltip from '@mui/material/Tooltip';
import { styled } from '@mui/material/styles';
import { Box, CircularProgress, Grid, Typography } from '@mui/material';
import { PauseIcon, PlayIcon } from '@/public/icon';
import VolumeUp from '@mui/icons-material/VolumeUp';
import VolumeDown from '@mui/icons-material/VolumeDown';
import VolumeMute from '@mui/icons-material/VolumeMute';
import TrackBar from './TrackBar';
import PauseIconBottom from '@mui/icons-material/Pause';
import PlayArrowIconBottom from '@mui/icons-material/PlayArrow';

const ControlsWrapper = styled('div')(({ theme }) => ({
    visibility: 'hidden',
    position: 'absolute',
    transition: 'visibility 0s linear 200ms, opacity 200ms',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    background: 'rgba(0,0,0,0.4)',
    display: 'flex',
    zIndex: 999,
    flexDirection: 'column',
    justifyContent: 'space-between',
}));

interface ControlsProps {
    onSeek: (event: any, value: number | number[]) => void;
    onSeekMouseDown: (event: any) => void;
    onSeekMouseUp: (event: any, value: number | number[]) => void;
    onDuration: (duration: number) => void;
    onRewind: () => void;
    onPlayPause: () => void;
    onFastForward: () => void;
    playing: boolean;
    played: number;
    elapsedTime: string;
    totalDuration: string;
    onMute: () => void;
    muted: boolean;
    isLoading: boolean;
    onVolumeSeekDown: (event: any, value: number | number[]) => void;
    onChangeDisplayFormat: () => void;
    playbackRate: number;
    onPlaybackRateChange: (rate: number) => void;
    onToggleFullScreen: () => void;
    volume: number;
    onVolumeChange: (event: any, value: number | number[]) => void;
    onBookmark: () => void;
    handleMouseMoveOnSlider?: () => void;
    handleMouseLeaveOnSlider?: () => void;
}

const Controls = forwardRef<HTMLDivElement, ControlsProps>(
    (
        {
            onSeek,
            onSeekMouseDown,
            onSeekMouseUp,
            onDuration,
            onRewind,
            onPlayPause,
            onFastForward,
            playing,
            played,
            elapsedTime,
            totalDuration,
            onMute,
            muted,
            isLoading,
            onVolumeSeekDown,
            onChangeDisplayFormat,
            playbackRate,
            onPlaybackRateChange,
            onToggleFullScreen,
            volume,
            onVolumeChange,
            onBookmark,
            handleMouseMoveOnSlider,
            handleMouseLeaveOnSlider,
        },
        ref
    ) => {
        const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);

        const handleClick = (event: React.MouseEvent<HTMLElement>) => {
            setAnchorEl(event.currentTarget);
        };

        const handleClose = () => {
            setAnchorEl(null);
        };
        const onSeeks = (e: Event, newValue: number | number[]) => {
            onSeek?.(e, newValue);
        };
        
        return (
            <ControlsWrapper
                ref={ref}
                sx={{ zIndex: 999 }}
            >
                <Grid
                    container
                    direction="column"
                    justifyContent="space-between"
                    alignItems="center"
                    sx={{ flexGrow: 1, zIndex: 999 }}
                >
                    <Grid
                        container
                        direction="row"
                        alignItems="center"
                        justifyContent="space-between"
                        // sx={{ padding: {mobile: 16, tablet: 11} }}
                    >
                        <Grid item></Grid>
                    </Grid>
                    <Grid
                        container
                        direction="row"
                        alignItems="center"
                        justifyContent="center"
                        sx={{
                            position: 'absolute',
                            top: '50%',
                            left: '50%',
                            transform: 'translate(-50%, -50%)',
                        }}
                    >
                        {playing && (
                            <IconButton
                                sx={{
                                    color: '#f5f5f5',
                                    '&:hover': {
                                        color: '#fff',
                                    },
                                }}
                                onClick={onRewind}
                                aria-label="rewind"
                            >
                                <FastRewindIcon fontSize="inherit" />
                            </IconButton>
                        )}

                        <IconButton
                            onClick={onPlayPause}
                            aria-label="play"
                        >
                            {playing ? <PauseIcon /> : <PlayIcon />}
                        </IconButton>
                        {playing && (
                            <IconButton
                                onClick={onFastForward}
                                aria-label="forward"
                                sx={{
                                    color: '#f5f5f5',
                                    '&:hover': {
                                        color: '#fff',
                                    },
                                }}
                            >
                                <FastForwardIcon fontSize="inherit" />
                            </IconButton>
                        )}
                    </Grid>
                    {/* bottom controls */}
                    <Grid
                        container
                        direction="row"
                        justifyContent="space-between"
                        alignItems="center"
                        sx={{ paddingRight: 4, paddingLeft: 4, zIndex: 18 }}
                        onMouseMove={handleMouseMoveOnSlider}
                        onMouseLeave={handleMouseLeaveOnSlider}
                    >
                        <Grid
                            item
                            mobile={12}
                            sx={{ zIndex: 18, position: 'relative' }}
                        >
                            <TrackBar
                                size="small"
                                min={0}
                                max={100}
                                name="process_video"
                                value={played * 100}
                                sx={{ py: 1 }}
                                onChange={onSeeks}
                                onChangeCommitted={onSeekMouseUp}
                            />
                        </Grid>
                        <Grid
                            item
                            flexDirection={'row'}
                        >
                            <Grid
                                container
                                alignItems="center"
                            >
                                <IconButton
                                    onClick={onPlayPause}
                                    sx={{
                                        color: '#f5f5f5',
                                        '&:hover': {
                                            color: '#fff',
                                        },
                                    }}
                                >
                                    {playing ? <PauseIconBottom /> : <PlayArrowIconBottom />}
                                </IconButton>
                                <IconButton
                                    onClick={onMute}
                                    sx={{
                                        color: '#f5f5f5',
                                        '&:hover': {
                                            color: '#fff',
                                        },
                                    }}
                                >
                                    {muted ? <VolumeMute fontSize="large" /> : volume > 0.5 ? <VolumeUp fontSize="large" /> : <VolumeDown fontSize="large" />}
                                </IconButton>
                                <Box>
                                    <TrackBar
                                        size="small"
                                        min={0}
                                        max={100}
                                        sx={{ width: 100 }}
                                        value={muted ? 0 : volume * 100}
                                        onChange={onVolumeChange}
                                        aria-labelledby="input-slider"
                                        onMouseDown={onSeekMouseDown}
                                        onChangeCommitted={onVolumeSeekDown}
                                    />
                                </Box>
                                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', lineHeight: '100%' }}>
                                    <Typography
                                        variant="h2_10Regular"
                                        style={{ color: '#fff', marginLeft: 16, textAlign: 'center' }}
                                    >
                                        {elapsedTime} / {totalDuration}
                                    </Typography>
                                </Box>
                            </Grid>
                        </Grid>
                    </Grid>
                </Grid>
            </ControlsWrapper>
        );
    }
);

export default Controls;
